/**
 * Types and functions for reading and writing Doc (.home files). There are also functions for verifying the integrity of
 * Doc's here.
 * @module
 */
import type { Annotation } from "building-types/annotation/annotationTypes";
import type { Doc } from "building-types/doc";
import type { RWTable } from "common/data/table";
import { marshalTypedArraysInObject } from "common/utils/marshalTypedArraysInObject";
import { stringifyWithTypedArraysInObject } from "common/utils/stringifyWithTypedArraysInObject";
import { defaultDoc, getDefaultDocFillTemplate } from "./docDefault";
import { docSyncSettingsOptionSets } from "./docSyncFields";
import { docMergeElevationOptionSet, docRepairElevationDefault } from "./elevation";
import docEntityGetParent from "./entity/docEntityGetParent";
import { docGetEntityHandleByHash } from "./entity/docEntityHandleHash";
import docGetEntity from "./entity/docGetEntity";
import { docCheckIntegrity } from "./integrity";
import type { DocIntegrityIssue } from "./integrityTypes";
import { IntegrityIssueType } from "./integrityTypes";
import { CURRENT_SCHEMA_VERSION, docMigrate } from "./migrate/docMigrate";

export const FILE_EXT = ".home";

/**
 * This function should be used in most cases, such as reading a Doc from the database. It parses and migrates
 * the Doc, and returns a copy. The serialize argument should be used when storing a Doc or writing to a file.
 *
 * @param {any} content Unparsed Studio Document
 * @param {boolean?} serialize Whether or not to first run the Doc through serialization, stripping the "isParsed" field and converting typed arrays to untyped ones
 * @param {string?} maxSchemaVersion Optional max schema version to stop migrations at
 * @param {string[]?} warnings A mutable array to put warnings in
 * @param {string[]?} errors A mutable array to put errors in
 *
 * @returns {Doc} The parsed & migrated Doc copy
 */
export default function docMarshal(
  content: any,
  serialize: boolean = false,
  maxSchemaVersion: string = CURRENT_SCHEMA_VERSION,
  warnings: string[] = [],
  errors: string[] = [],
): Doc {
  const parsed = docParseJSON(content, maxSchemaVersion, warnings, errors);

  if (serialize) {
    return marshalTypedArraysInObject(docMarkUnparsed(parsed)) as Doc;
  }

  return parsed;
}

export function docParseBuffer(content: Buffer): Doc {
  return marshalTypedArraysInObject(docMarkUnparsed(docParse(content.toString("utf-8")))) as Doc;
}

export function docParseForDownload(content: any): string {
  return docSerialize(docParseJSON(content));
}

export function docParse(content: string, warnings: string[] = [], errors: string[] = []): Doc {
  return docParseJSON(JSON.parse(content), undefined, warnings, errors);
}

export function docParseCheckIntegrity(content: string): [Doc, DocIntegrityIssue[]] {
  return docParseJSONCheckIntegrity(JSON.parse(content));
}

export function docParseJSON(
  content: any,
  maxSchemaVersion: string = CURRENT_SCHEMA_VERSION,
  warnings: string[] = [],
  errors: string[] = [],
): Doc {
  return docParseJSONCheckIntegrity(content, maxSchemaVersion, warnings, errors)[0];
}

export function docParseJSONCheckIntegrity(
  content: any,
  maxSchemaVersion: string = CURRENT_SCHEMA_VERSION,
  warnings: string[] = [],
  errors: string[] = [],
): [Doc, DocIntegrityIssue[]] {
  if (!content) {
    return [defaultDoc(), [{ type: IntegrityIssueType.DOC_EMPTY, message: "Document is empty" }]];
  }

  let migratedDoc: Doc | undefined;
  try {
    migratedDoc = docMigrate(content, maxSchemaVersion, warnings, errors);
  } catch (e) {
    console.error(e);
    // not parsing any further
    return [
      defaultDoc(content.name),
      [{ type: IntegrityIssueType.INVALID_DOC_VERSION, message: e.message ?? "Document migration failed", content }],
    ];
  }

  let doc: Doc = { ...getDefaultDocFillTemplate(), ...migratedDoc };

  // gc the hidden & locked fields
  const { hidden, locked } = doc;

  const lockedFiltered = {};
  const hiddenFiltered = {};

  for (const hash in hidden) {
    const cur = hidden[hash];
    if (docGetEntity(doc, cur)) {
      hiddenFiltered[hash] = cur;
    }
  }

  for (const hash in locked) {
    const cur = locked[hash];
    if (docGetEntity(doc, cur)) {
      lockedFiltered[hash] = cur;
    }
  }

  doc = { ...doc, hidden: hiddenFiltered, locked: lockedFiltered };
  doc = docSyncSettingsOptionSets(doc);
  doc = docMergeElevationOptionSet(doc);
  // Heals docs written before the mirror stopped repairing an orphaned elevation default.
  doc = docRepairElevationDefault(doc);

  // now that we've parsed the doc, we can mark it as parsed
  doc = { ...doc, isParsed: true };

  // Fix known room layout issues caused by doc ref bugs earlier
  // TODO: Remove this when the doc ref integrity project has a grip on the issue
  doc = docCleanupOrphanedRoomLayoutAnnotations(doc, warnings);

  return [doc, docCheckIntegrity(doc)];
}

/** deprecated DO NOT USE
 * @deprecated
 *
 * TODO: Remove this when the doc ref integrity project has a grip on the issue
 */
function docCleanupOrphanedRoomLayoutAnnotations(doc: Doc, warnings: string[]) {
  // Clean up entity groups
  const newEntityGroups: RWTable<string> = {};
  for (const hash in doc.entityGroups) {
    const groupId = doc.entityGroups[hash];
    const group = doc.groups?.[groupId];

    if (!group) {
      warnings.push(`Entity reference group ${groupId} not found, removed`);
      continue;
    }

    if (group.entityType !== "GROUP") {
      warnings.push(`Entity reference group ${groupId} is not a GROUP, removed`);
      continue;
    }

    if (!docGetEntityHandleByHash(doc, hash)) {
      warnings.push(`Entity ${hash} for entity reference group ${groupId} not found, removed`);
      continue;
    }

    newEntityGroups[hash] = groupId;
  }

  // Clean up annotations
  const newAnnotation: RWTable<Annotation> = {};
  for (const annotationId in doc.annotation) {
    const annotation = doc.annotation[annotationId];

    const parent = docEntityGetParent(doc, { type: "ANNOTATION", id: annotationId });

    if (!parent) {
      warnings.push(`Annotation parent for ${annotationId} not found, removed`);
      continue;
    }

    if (annotation.id !== annotationId) {
      warnings.push(`Annotation ${annotationId} has id ${annotation.id} but is keyed by ${annotationId}, removed`);
      continue;
    }

    if (annotation.entityType !== "ANNOTATION") {
      warnings.push(`Annotation ${annotationId} is not an ANNOTATION, removed`);
      continue;
    }

    newAnnotation[annotationId] = annotation;
  }

  // Clean up entityRoomLayouts
  const newEntityRoomLayouts: RWTable<string> = {};
  for (const hash in doc.entityRoomLayouts) {
    if (!docGetEntityHandleByHash(doc, hash)) {
      warnings.push(`Entity ${hash} for entityRoomLayouts not found, removed`);
      continue;
    }

    newEntityRoomLayouts[hash] = doc.entityRoomLayouts[hash];
  }
  doc = { ...doc, entityRoomLayouts: newEntityRoomLayouts, annotation: newAnnotation, entityGroups: newEntityGroups };
  return doc;
}

/**
 * docSerialize transforms a Doc into a string that can be written to a file.
 * This is essentially a JSON.stringify, but it also handles typed arrays.
 */
export function docSerialize(doc: Doc): string {
  return stringifyWithTypedArraysInObject(docMarkUnparsed(doc));
}

export function docMarkUnparsed(doc: Doc): Omit<Doc, "isParsed"> {
  const docCopy = { ...doc } as any;
  delete docCopy.isParsed;
  return docCopy;
}

/**
 * Serializes a Doc wrapping it in a wrapper object with configurable fields.
 */
export function docSerializeWithWrapper(doc: Doc, wrapperFields: any): string {
  return stringifyWithTypedArraysInObject({
    doc: docMarkUnparsed(doc),
    ...wrapperFields,
  });
}
